import React, { useEffect, useState } from 'react'
import { apiFetch } from './api';
import { useNavigate } from 'react-router-dom'

function AdminCreateUser({onCreate}){
  const [username,setUsername]=useState('')
  const [password,setPassword]=useState('')
  const [role,setRole]=useState('OPERATOR')
  const [supervisorId,setSupervisorId]=useState('')
  const [supervisorCompany,setSupervisorCompany]=useState('')
  const [supervisors,setSupervisors]=useState([])
  const [supervisorLoadError,setSupervisorLoadError]=useState(null)
  const [loading,setLoading]=useState(false)
  const [msg,setMsg]=useState(null)
  const [msgTone,setMsgTone]=useState('info')

  useEffect(() => {
    if (role !== 'SUPERVISOR') {
      setSupervisors([])
      setSupervisorCompany('')
      setSupervisorId('')
      return
    }
    let ignore = false
    setSupervisorLoadError(null)
    apiFetch('/api/supervisors', { credentials:'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('failed')))
      .then(data => { if (!ignore) setSupervisors(Array.isArray(data) ? data : []) })
      .catch(() => { if (!ignore) setSupervisorLoadError('Unable to load supervisors'); setSupervisors([]) })
    return () => { ignore = true }
  }, [role])

  const submit = async e =>{
    e.preventDefault()
    setMsg(null)
    setMsgTone('info')
    setLoading(true)
    try {
      const payload = {username,password,role}
      if (role === 'SUPERVISOR' && supervisorId) payload.supervisorId = supervisorId
      const res = await apiFetch('/api/admin/users', {method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify(payload)})
      if (res.ok){ setMsgTone('success'); setMsg('User created'); setUsername(''); setPassword(''); setSupervisorId(''); setSupervisorCompany(''); onCreate && onCreate() }
      else { const b = await res.json().catch(()=>({error:'failed'})); setMsgTone('error'); setMsg(b.error||'Failed to create user') }
    } catch(e){ setMsgTone('error'); setMsg('Network error') }
    finally{ setLoading(false) }
  }

  const companyOptions = Array.from(new Set(supervisors.map(s => s.company).filter(Boolean))).sort()
  const filteredSupervisors = supervisors.filter(s => !supervisorCompany || s.company === supervisorCompany)

  return (
    <form onSubmit={submit} style={{display:'grid',gridTemplateColumns:'1fr',gap:12}}>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        <label style={{fontSize:13,color:'#4a5568'}}>Username</label>
        <input placeholder="username" value={username} onChange={e=>setUsername(e.target.value)} required style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}} />
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        <label style={{fontSize:13,color:'#4a5568'}}>Password</label>
        <input placeholder="password" value={password} onChange={e=>setPassword(e.target.value)} required type="password" style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}} />
        <span style={{fontSize:12,color:'#6b7280'}}>Use a strong passphrase for admin accounts.</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        <label style={{fontSize:13,color:'#4a5568'}}>Role</label>
        <select value={role} onChange={e=>{setRole(e.target.value); if (e.target.value !== 'SUPERVISOR') { setSupervisorId(''); setSupervisorCompany(''); } }} style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}}>
          <option value="OPERATOR">Operator</option>
          <option value="SUPERVISOR">Supervisor</option>
          <option value="ADMIN">Admin</option>
        </select>
      </div>
      {role === 'SUPERVISOR' && (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontSize:13,color:'#4a5568'}}>Company</label>
            <select value={supervisorCompany} onChange={e=>{setSupervisorCompany(e.target.value); setSupervisorId('')}} required style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}}>
              <option value="" disabled>Select company</option>
              {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            <label style={{fontSize:13,color:'#4a5568'}}>Supervisor</label>
            <select value={supervisorId} onChange={e=>setSupervisorId(e.target.value)} required style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}} disabled={!supervisorCompany || filteredSupervisors.length === 0}>
              <option value="" disabled>{supervisorCompany ? 'Select supervisor' : 'Select company first'}</option>
              {filteredSupervisors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span style={{fontSize:12,color:'#6b7280'}}>Assign this user to an existing supervisor record.</span>
            {supervisorLoadError && <span style={{fontSize:12,color:'#c0392b'}}>{supervisorLoadError}</span>}
          </div>
        </div>
      )}
      <div style={{display:'flex',gap:10,alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <button className="btn primary" disabled={!username||!password||loading}>{loading? 'Creating…':'Create user'}</button>
          {msg && (
            <span style={{color: msgTone==='error' ? '#c0392b' : '#2563eb', fontSize:13}}>{msg}</span>
          )}
        </div>
        <span style={{fontSize:12,color:'#6b7280'}}>Roles: admin = full access, operator = limited.</span>
      </div>
    </form>
  )
}

function AdminUserList({users}){
  if (!users) return <div>Loading…</div>

  const labelForRoles = roles => {
    if (!roles) return 'unknown'
    if (roles.includes('ROLE_ADMIN')) return 'admin'
    if (roles.includes('ROLE_SUPERVISOR')) return 'supervisor'
    if (roles.includes('ROLE_OPERATOR')) return 'operator'
    return 'user'
  }

  return (
    <ul style={{listStyle:'none',padding:0,margin:0,display:'flex',flexDirection:'column',gap:8}}>
      {users.map(u => (
        <li key={u.username} style={{padding:'10px 12px',border:'1px solid #e7ecf3',borderRadius:10,display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fdfdff'}}>
          <div style={{fontWeight:600,color:'#1f2937'}}>{u.username}</div>
          <span style={{background:'#ecf2ff',color:'#1d4ed8',borderRadius:999,padding:'4px 10px',fontSize:12,fontWeight:700,textTransform:'capitalize'}}>{labelForRoles(u.roles)}</span>
        </li>
      ))}
    </ul>
  )
}

export default function AdminPanel(){
  const [user,setUser]=useState(null)
  const [loading,setLoading]=useState(true)
  const [loggingOut,setLoggingOut]=useState(false)
  const [users,setUsers]=useState(null)
  const [section,setSection]=useState('create')
  const [supervisors,setSupervisors]=useState([])
  const [supLoadError,setSupLoadError]=useState(null)
  const [reassignUser,setReassignUser]=useState('')
  const [reassignSupervisor,setReassignSupervisor]=useState('')
  const [reassignLoading,setReassignLoading]=useState(false)
  const [reassignMsg,setReassignMsg]=useState(null)
  const [reassignTone,setReassignTone]=useState('info')
  const [sidebarOpen,setSidebarOpen]=useState(true)
  const nav = useNavigate()

  const logout = async () => {
    setLoggingOut(true)
    try {
      await apiFetch('/api/logout', {method: 'POST', credentials: 'include'})
    } catch (e) {
      console.warn('Logout request failed', e)
    } finally {
      nav('/login')
    }
  }

  const fetchUsers = async ()=>{
    try{
      const r = await apiFetch('/api/admin/users', {credentials:'include'})
      if (r.ok) setUsers(await r.json())
    }catch(e){}
  }

  const fetchSupervisors = async ()=>{
    try{
      const r = await apiFetch('/api/supervisors', {credentials:'include'})
      if (r.ok) setSupervisors(await r.json())
      else setSupLoadError('Unable to load supervisors')
    }catch(e){ setSupLoadError('Unable to load supervisors') }
  }

  useEffect(()=>{
    document.title = 'Admin panel'
    apiFetch('/api/me', {credentials:'include'})
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data){
          nav('/login')
          return
        }
        if (!data.roles || !data.roles.includes('ROLE_ADMIN')){
          nav('/dashboard')
          return
        }
        setUser(data)
        fetchUsers()
        fetchSupervisors()
      })
      .finally(()=>setLoading(false))
  },[])

  const handleReassign = async e => {
    e.preventDefault()
    setReassignMsg(null)
    if (!reassignUser || !reassignSupervisor) return
    setReassignLoading(true)
    try{
      const res = await apiFetch('/api/admin/reassign-supervisor', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        credentials:'include',
        body: JSON.stringify({ username: reassignUser, supervisorId: reassignSupervisor })
      })
      if (res.ok){
        setReassignTone('success')
        setReassignMsg('Supervisor reassigned')
        setReassignUser('')
        setReassignSupervisor('')
        fetchUsers()
      } else {
        const b = await res.json().catch(()=>({error:'Failed'}))
        setReassignTone('error')
        setReassignMsg(b.error || 'Failed to reassign')
      }
    } catch(err){
      setReassignTone('error')
      setReassignMsg('Network error')
    } finally {
      setReassignLoading(false)
    }
  }

  if (loading) return <div className="dashboard-root">Loading admin panel…</div>

  return (
    <div style={{display:'flex', minHeight:'100vh', background:'#f3f6ff', color:'#0f172a'}}>
      <aside style={{width: sidebarOpen ? 260 : 84, transition:'width 200ms ease', background:'#eef2ff', borderRight:'1px solid #d8def0', display:'flex', flexDirection:'column', padding: sidebarOpen ? '22px 18px' : '14px 14px 18px', gap:18, position:'sticky', top:0, height:'100vh', boxShadow:'2px 0 12px rgba(15,23,42,0.06)'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent: sidebarOpen ? 'space-between' : 'center', gap:10}}>
          {sidebarOpen && (
            <div>
              <div style={{fontSize:12, letterSpacing:0.5, textTransform:'uppercase', color:'#415fbd'}}>Admin</div>
              <div style={{fontWeight:800}}>Control panel</div>
            </div>
          )}
          <button className="btn secondary" style={{minWidth: sidebarOpen ? undefined : 48, width: sidebarOpen ? undefined : '100%', padding:'12px 14px', borderColor:'#c3d0f7', background:'#dbe5ff', color:'#0f172a', fontWeight:700, borderRadius:12}} onClick={()=>nav('/dashboard')} aria-label="Back to dashboard">{sidebarOpen ? 'Back' : '↩'}</button>
        </div>

        <nav style={{display:'grid', gap:12}}>
          <button className="btn secondary" style={{justifyContent:'flex-start', padding:'14px 16px', fontWeight:800, fontSize:15, borderRadius:14, background: section==='create' ? '#dbe5ff' : '#eef2ff', borderColor: section==='create' ? '#b8c8ff' : '#d8def0', color:'#0f172a', boxShadow: section==='create' ? '0 6px 18px rgba(65,95,189,0.18)' : 'none'}} onClick={()=>setSection('create')} aria-pressed={section==='create'}>
            {sidebarOpen ? 'Create users' : 'C'}
          </button>
          <button className="btn secondary" style={{justifyContent:'flex-start', padding:'14px 16px', fontWeight:800, fontSize:15, borderRadius:14, background: section==='existing' ? '#dbe5ff' : '#eef2ff', borderColor: section==='existing' ? '#b8c8ff' : '#d8def0', color:'#0f172a', boxShadow: section==='existing' ? '0 6px 18px rgba(65,95,189,0.18)' : 'none'}} onClick={()=>{setSection('existing'); if(!users) fetchUsers();}} aria-pressed={section==='existing'}>
            {sidebarOpen ? 'Existing users' : 'E'}
          </button>
          <button className="btn secondary" style={{justifyContent:'flex-start', padding:'14px 16px', fontWeight:800, fontSize:15, borderRadius:14, background: section==='reassign' ? '#dbe5ff' : '#eef2ff', borderColor: section==='reassign' ? '#b8c8ff' : '#d8def0', color:'#0f172a', boxShadow: section==='reassign' ? '0 6px 18px rgba(65,95,189,0.18)' : 'none'}} onClick={()=>{setSection('reassign'); if(!users) fetchUsers(); if(supervisors.length===0 && !supLoadError) fetchSupervisors();}} aria-pressed={section==='reassign'}>
            {sidebarOpen ? 'Reassign supervisor' : 'R'}
          </button>
          <button className="btn secondary" style={{justifyContent:'flex-start', padding:'14px 16px', fontWeight:800, fontSize:15, borderRadius:14, background: section==='logs' ? '#dbe5ff' : '#eef2ff', borderColor: section==='logs' ? '#b8c8ff' : '#d8def0', color:'#0f172a', boxShadow: section==='logs' ? '0 6px 18px rgba(65,95,189,0.18)' : 'none'}} onClick={()=>setSection('logs')} aria-pressed={section==='logs'}>
            {sidebarOpen ? 'Logs' : 'L'}
          </button>
        </nav>

      </aside>

      <main style={{flex:1, background:'#f8f9fb', color:'#0f172a', padding:'34px 28px 32px'}}>
        <header style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, marginBottom:26}}>
          <div>
            <div style={{fontSize:12, letterSpacing:0.4, textTransform:'uppercase', color:'#4b5563'}}>Admin panel</div>
            <h1 style={{margin:'6px 0 4px', fontSize:28, fontWeight:800}}>Manage users and logs</h1>
            <div style={{fontSize:13, color:'#6b7280'}}>Signed in as {user?.username}</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <button className="btn secondary" onClick={()=>nav('/dashboard')} style={{padding:'10px 16px', background:'#dbe5ff', borderColor:'#c3d0f7', color:'#0f172a', fontWeight:700, borderRadius:12}}>Back</button>
            <button className="btn secondary" onClick={()=>nav('/change-password')} style={{padding:'10px 16px', background:'#e7ecff', borderColor:'#cfd9ff', color:'#0f172a', fontWeight:700, borderRadius:12}}>Change password</button>
            <button className="btn secondary" onClick={logout} disabled={loggingOut} aria-busy={loggingOut} style={{padding:'10px 16px', background:'#e7ecff', borderColor:'#cfd9ff', color:'#0f172a', fontWeight:700, borderRadius:12}}>{loggingOut ? 'Logging out…' : 'Logout'}</button>
          </div>
        </header>

        <section style={{background:'#ffffff', border:'1px solid #e7ecf3', borderRadius:16, padding:20, boxShadow:'0 12px 36px rgba(0,0,0,0.08)', minHeight:420}}>
          {section === 'create' && (
            <div style={{display:'grid', gap:14}}>
              <div style={{fontSize:14, color:'#374151', fontWeight:600}}>Create users</div>
              <div style={{fontSize:13, color:'#6b7280'}}>Create new accounts for operators, supervisors, or admins.</div>
              <AdminCreateUser onCreate={()=>fetchUsers()} />
              {/* Download options removed as requested */}
            </div>
          )}
          {section === 'existing' && (
            <div style={{display:'grid', gap:14}}>
              <div style={{fontSize:14, color:'#374151', fontWeight:600}}>Existing users</div>
              <div style={{fontSize:13, color:'#6b7280'}}>Review who has access. Roles are shown per user.</div>
              <div style={{maxHeight:'60vh', overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#f9fafb'}}>
                <AdminUserList users={users} />
              </div>
            </div>
          )}
          {section === 'reassign' && (
            <div style={{display:'grid', gap:14}}>
              <div style={{fontSize:14, color:'#374151', fontWeight:600}}>Reassign supervisor user</div>
              <div style={{fontSize:13, color:'#6b7280'}}>Pick an existing supervisor account and map it to a different supervisor record.</div>
              <form onSubmit={handleReassign} style={{display:'grid', gap:12, maxWidth:520}}>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  <label style={{fontSize:13,color:'#4a5568'}}>Supervisor user</label>
                  <select value={reassignUser} onChange={e=>setReassignUser(e.target.value)} required style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}}>
                    <option value="" disabled>Select supervisor user</option>
                    {(users||[]).filter(u => u.roles && u.roles.includes('ROLE_SUPERVISOR')).map(u => (
                      <option key={u.username} value={u.username}>{u.username}</option>
                    ))}
                  </select>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  <label style={{fontSize:13,color:'#4a5568'}}>Target supervisor</label>
                  <select value={reassignSupervisor} onChange={e=>setReassignSupervisor(e.target.value)} required style={{padding:10,borderRadius:8,border:'1px solid #dfe6f3'}} disabled={supervisors.length===0}>
                    <option value="" disabled>{supervisors.length===0 ? 'No supervisors loaded' : 'Select supervisor record'}</option>
                    {supervisors.map(s => <option key={s.id} value={s.id}>{s.name} ({s.company})</option>)}
                  </select>
                  {supLoadError && <span style={{fontSize:12,color:'#c0392b'}}>{supLoadError}</span>}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <button className="btn primary" disabled={!reassignUser||!reassignSupervisor||reassignLoading}>{reassignLoading ? 'Saving…' : 'Reassign'}</button>
                  {reassignMsg && <span style={{color: reassignTone==='error' ? '#c0392b' : '#2563eb', fontSize:13}}>{reassignMsg}</span>}
                </div>
              </form>
            </div>
          )}
          {section === 'logs' && (
            <div style={{display:'grid', gap:14}}>
              <div style={{fontSize:14, color:'#374151', fontWeight:600}}>Logs</div>
              <div style={{fontSize:13, color:'#6b7280'}}>Open the log console to view recent events and errors.</div>
              <button className="btn primary" onClick={()=>nav('/secret-admin')}>Go to logs</button>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
